import React, { useState, useCallback, useEffect } from 'react';
import { Trophy, X, Play, RotateCcw, ChevronRight } from 'lucide-react';

const TournamentMode = ({ entries, onClose }) => {
  const [bracket, setBracket] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const [winner, setWinner] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [matchResult, setMatchResult] = useState(null);

  // Initialize bracket from entries
  useEffect(() => {
    if (entries.length < 2) return;
    
    const enabledEntries = entries.filter(e => e.enabled !== false);
    
    // Shuffle entries
    const shuffled = [...enabledEntries];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const randomValues = new Uint32Array(1);
      crypto.getRandomValues(randomValues);
      const j = randomValues[0] % (i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Pad to power of 2
    let size = 2;
    while (size < shuffled.length) size *= 2;
    while (shuffled.length < size) {
      shuffled.push({ id: `bye-${shuffled.length}`, text: 'BYE', color: '#ccc', isBye: true });
    }

    // Create initial bracket
    const initialBracket = [];
    for (let i = 0; i < shuffled.length; i += 2) {
      initialBracket.push({
        id: `match-0-${i/2}`,
        player1: shuffled[i],
        player2: shuffled[i + 1],
        winner: null
      });
    }

    setBracket([initialBracket]);
    setCurrentRound(0);
    setCurrentMatch(0);
    setWinner(null);
    
    // Auto-advance BYE matches
    advanceByes([initialBracket]);
  }, [entries]);

  // Auto-advance BYE matches
  const advanceByes = useCallback((currentBracket) => {
    const round = currentBracket[0];
    const updatedRound = round.map(match => {
      if (match.player1.isBye && !match.player2.isBye) {
        return { ...match, winner: match.player2 };
      }
      if (match.player2.isBye && !match.player1.isBye) {
        return { ...match, winner: match.player1 };
      }
      if (match.player1.isBye && match.player2.isBye) {
        return { ...match, winner: match.player1 };
      }
      return match;
    });
    
    if (JSON.stringify(round) !== JSON.stringify(updatedRound)) {
      setBracket([updatedRound, ...currentBracket.slice(1)]);
    }
  }, []);

  // Spin to determine match winner
  const spinMatch = useCallback(() => {
    const match = bracket[currentRound]?.[currentMatch];
    if (!match || match.winner) return;

    setIsSpinning(true);
    setMatchResult(null);

    // Simulate spin with crypto random
    setTimeout(() => {
      const randomValues = new Uint32Array(1);
      crypto.getRandomValues(randomValues);
      const winnerIndex = randomValues[0] % 2;
      const matchWinner = winnerIndex === 0 ? match.player1 : match.player2;
      
      // Update bracket
      const updatedBracket = [...bracket];
      updatedBracket[currentRound] = [...updatedBracket[currentRound]];
      updatedBracket[currentRound][currentMatch] = {
        ...match,
        winner: matchWinner
      };

      setBracket(updatedBracket);
      setMatchResult(matchWinner);
      setIsSpinning(false);

      // Check if round is complete
      const roundComplete = updatedBracket[currentRound].every(m => m.winner);
      
      if (roundComplete) {
        const winners = updatedBracket[currentRound].map(m => m.winner);
        
        if (winners.length === 1) {
          // Tournament complete!
          setWinner(winners[0]);
        } else {
          // Create next round
          const nextRound = [];
          for (let i = 0; i < winners.length; i += 2) {
            nextRound.push({
              id: `match-${currentRound + 1}-${i/2}`,
              player1: winners[i],
              player2: winners[i + 1] || { text: 'BYE', isBye: true },
              winner: null
            });
          }
          updatedBracket.push(nextRound);
          setBracket(updatedBracket);
        }
      }
    }, 1500);
  }, [bracket, currentRound, currentMatch]);

  // Find next match to play
  const findNextMatch = useCallback(() => {
    for (let r = 0; r < bracket.length; r++) {
      for (let m = 0; m < bracket[r].length; m++) {
        if (!bracket[r][m].winner && !bracket[r][m].player1.isBye && !bracket[r][m].player2.isBye) {
          setCurrentRound(r);
          setCurrentMatch(m);
          return { round: r, match: m };
        }
      }
    }
    return null;
  }, [bracket]);

  // Auto-find next match after result
  useEffect(() => {
    if (matchResult && !winner) {
      setTimeout(() => {
        findNextMatch();
        setMatchResult(null);
      }, 2000);
    }
  }, [matchResult, winner, findNextMatch]);

  const currentMatchData = bracket[currentRound]?.[currentMatch];
  const enabledCount = entries.filter(e => e.enabled !== false).length;

  if (enabledCount < 2) {
    return (
      <div className="tournament-empty">
        <Trophy size={32} />
        <p>Need at least 2 entries for tournament</p>
      </div>
    );
  }

  return (
    <div className="tournament-container">
      <div className="tournament-header">
        <h2>🏆 Tournament Mode</h2>
        <button onClick={onClose} className="btn-close-tournament">
          <X size={20} />
        </button>
      </div>

      {winner ? (
        <div className="tournament-winner">
          <div className="winner-trophy">🏆</div>
          <h3>Champion!</h3>
          <div 
            className="winner-name"
            style={{ backgroundColor: winner.color }}
          >
            {winner.text}
          </div>
          <button 
            onClick={() => {
              setBracket([]);
              setWinner(null);
            }}
            className="btn-restart"
          >
            <RotateCcw size={16} />
            New Tournament
          </button>
        </div>
      ) : (
        <>
          {/* Current Match */}
          {currentMatchData && !currentMatchData.winner && (
            <div className="current-match">
              <h3>Round {currentRound + 1} - Match {currentMatch + 1}</h3>
              <div className="match-versus">
                <div 
                  className={`match-player ${matchResult === currentMatchData.player1 ? 'winner' : ''}`}
                  style={{ borderColor: currentMatchData.player1.color }}
                >
                  <span 
                    className="player-color"
                    style={{ backgroundColor: currentMatchData.player1.color }}
                  />
                  {currentMatchData.player1.text}
                </div>
                <span className="vs">VS</span>
                <div 
                  className={`match-player ${matchResult === currentMatchData.player2 ? 'winner' : ''}`}
                  style={{ borderColor: currentMatchData.player2.color }}
                >
                  <span 
                    className="player-color"
                    style={{ backgroundColor: currentMatchData.player2.color }}
                  />
                  {currentMatchData.player2.text}
                </div>
              </div>
              
              {!matchResult && (
                <button 
                  onClick={spinMatch} 
                  className="btn-spin-match"
                  disabled={isSpinning}
                >
                  {isSpinning ? 'Spinning...' : 'SPIN!'}
                </button>
              )}
              
              {matchResult && (
                <div className="match-result">
                  <span>Winner:</span>
                  <strong style={{ color: matchResult.color }}>{matchResult.text}</strong>
                </div>
              )}
            </div>
          )}

          {/* Bracket Visualization */}
          <div className="bracket-visual">
            {bracket.map((round, rIndex) => (
              <div key={rIndex} className="bracket-round">
                <div className="round-label">Round {rIndex + 1}</div>
                {round.map((match, mIndex) => (
                  <div 
                    key={match.id} 
                    className={`bracket-match ${rIndex === currentRound && mIndex === currentMatch ? 'current' : ''}`}
                  >
                    <div className={`bracket-player ${match.winner === match.player1 ? 'winner' : ''}`}>
                      <span 
                        className="player-dot"
                        style={{ backgroundColor: match.player1.isBye ? '#ccc' : match.player1.color }}
                      />
                      <span className="player-name">
                        {match.player1.text}
                      </span>
                    </div>
                    <div className={`bracket-player ${match.winner === match.player2 ? 'winner' : ''}`}>
                      <span 
                        className="player-dot"
                        style={{ backgroundColor: match.player2.isBye ? '#ccc' : match.player2.color }}
                      />
                      <span className="player-name">
                        {match.player2.text}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TournamentMode;
